// src/pages/Trend/components/analysis/market-trends/SearchTrendChart.js
import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceDot
} from 'recharts';
import './SearchTrendChart.css';
import { BBC_STYLE } from '../../../../../styles/chartStyles';
import ChartContainer from '../common/ChartContainer';

// Format number (rounded, no decimals, with comma separation)
const formatNumber = (value, translate) => {
  const currentLang = localStorage.getItem('language') || 'ko';

  if (currentLang === 'ko' && value >= 10000) {
    const manValue = Math.round(value / 10000);
    return `${manValue.toLocaleString()}만`;
  }
  return Math.round(value).toLocaleString();
};

// Create narrative title based on data insights
const createNarrativeTitle = (primaryData, secondaryData, hasSecondaryData, translate) => {
  const trend1 = primaryData.trend;
  const maxPeriod1 = primaryData.max_period.labels;

  if (!hasSecondaryData) {
    // Single narrative without comparison
    const narrativeKey = trend1.percentage >= 0 ? 'trendUp' : 'trendDown';
    const narrative = translate(`trendPage.marketTrends.narratives.search.single.${narrativeKey}`, {
      percentage: Math.abs(trend1.percentage).toFixed(1)
    });
    const peakText = translate('trendPage.marketTrends.narratives.search.single.peakPeriod', {
      period: maxPeriod1
    });

    return (
      <>
        {narrative}{', '}
        <span style={{
          fontWeight: '700',
          backgroundColor: 'rgba(19, 128, 161, 0.1)',
          padding: '0 4px',
          borderRadius: '2px'
        }}>
          {peakText}
        </span>
      </>
    );
  }

  // Comparison mode
  const trend2 = secondaryData.trend;
  const maxPeriod2 = secondaryData.max_period.labels;
  const bothRising = trend1.percentage >= 0 && trend2.percentage >= 0;
  const bothFalling = trend1.percentage < 0 && trend2.percentage < 0;

  // Combined insight
  const combinedInsight = bothRising ? translate('trendPage.marketTrends.narratives.search.comparison.bothRising') :
    bothFalling ? translate('trendPage.marketTrends.narratives.search.comparison.bothFalling') :
      translate('trendPage.marketTrends.narratives.search.comparison.differentTrends');

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    }}>
      {/* Combined insight first */}
      <div style={{
        fontSize: '1.05rem',
        fontWeight: '600',
        color: BBC_STYLE.TEXT_COLOR
      }}>
        {combinedInsight}
      </div>

      {/* Individual narratives */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '10px',
        fontSize: '0.85rem'
      }}>
        {/* Primary narrative */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: BBC_STYLE.PRIMARY_COLOR
          }} />
          <span>
            {translate('trendPage.filters.set1')}:{' '}
            <span style={{
              fontWeight: '700',
              color: trend1.percentage >= 0 ? BBC_STYLE.POSITIVE_COLOR : BBC_STYLE.NEGATIVE_COLOR
            }}>
              {trend1.percentage >= 0 ? '+' : ''}{trend1.percentage.toFixed(1)}%
            </span>
            {', '}{translate('trendPage.marketTrends.narratives.search.single.peakPeriod', { period: maxPeriod1 })}
          </span>
        </div>

        {/* Secondary narrative */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: BBC_STYLE.SECONDARY_COLOR
          }} />
          <span>
            {translate('trendPage.filters.set2')}:{' '}
            <span style={{
              fontWeight: '700',
              color: trend2.percentage >= 0 ? BBC_STYLE.POSITIVE_COLOR : BBC_STYLE.NEGATIVE_COLOR
            }}>
              {trend2.percentage >= 0 ? '+' : ''}{trend2.percentage.toFixed(1)}%
            </span>
            {', '}{translate('trendPage.marketTrends.narratives.search.single.peakPeriod', { period: maxPeriod2 })}
          </span>
        </div>

        {/* Trend difference indicator */}
        {Math.abs(trend1.percentage - trend2.percentage) > 10 && (
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '2px 8px',
            backgroundColor: 'rgba(213, 94, 0, 0.08)',
            borderRadius: '4px',
            fontSize: '0.85rem'
          }}>
            <span style={{
              fontWeight: '600',
              color: BBC_STYLE.HIGHLIGHT_COLOR
            }}>
              {translate('trendPage.marketTrends.narratives.search.comparison.trendDiff', {
                diff: Math.abs(trend1.percentage - trend2.percentage).toFixed(1)
              })}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// Create subtitle with key insights
const createSubtitle = (primaryData, secondaryData, hasSecondaryData, translate) => {
  const avgVolume1 = primaryData.average;
  const maxVolume1 = primaryData.max_period.values;
  const minVolume1 = primaryData.min_period.values;

  if (!hasSecondaryData) {
    // Single subtitle without comparison
    const avgText = translate('trendPage.marketTrends.narratives.search.single.avgVolume', {
      volume: formatNumber(avgVolume1, translate)
    });

    // Format min and max values with color highlights
    const minValue = formatNumber(minVolume1, translate);
    const maxValue = formatNumber(maxVolume1, translate);
    const minText = translate('trendPage.marketTrends.narratives.search.single.minVolume', {
      volume: '' // Empty placeholder - we'll insert the styled value
    }).trim();
    const maxText = translate('trendPage.marketTrends.narratives.search.single.maxVolume', {
      volume: '' // Empty placeholder - we'll insert the styled value
    }).trim();

    return (
      <>
        {avgText}
        <span style={{
          margin: '0 10px',
          color: '#D0D0D0',
          fontWeight: '300'
        }}>｜</span>
        <span style={{ color: BBC_STYLE.NEGATIVE_COLOR, fontWeight: '600' }}>↓</span>{' '}
        {minText.replace('{volume}', '').trim()}{' '}
        <span style={{
          color: BBC_STYLE.NEGATIVE_COLOR,
          fontWeight: '700',
          backgroundColor: 'rgba(213, 94, 0, 0.08)',
          padding: '2px 6px',
          borderRadius: '3px'
        }}>
          {minValue}
        </span>
        <span style={{
          margin: '0 10px',
          color: '#D0D0D0',
          fontWeight: '300'
        }}>｜</span>
        <span style={{ color: BBC_STYLE.POSITIVE_COLOR, fontWeight: '600' }}>↑</span>{' '}
        {maxText.replace('{volume}', '').trim()}{' '}
        <span style={{
          color: BBC_STYLE.POSITIVE_COLOR,
          fontWeight: '700',
          backgroundColor: 'rgba(46, 125, 50, 0.08)',
          padding: '2px 6px',
          borderRadius: '3px'
        }}>
          {maxValue}
        </span>
      </>
    );
  }

  // Comparison mode
  const avgVolume2 = secondaryData.average;
  const avgDiff = Math.abs(avgVolume1 - avgVolume2);
  const avgDiffPercentage = ((avgDiff / Math.min(avgVolume1, avgVolume2)) * 100).toFixed(0);

  return (
    <>
      {translate('trendPage.filters.set1')}: {' '}
      <span style={{
        fontWeight: '700',
        fontSize: '1.05rem',
        color: BBC_STYLE.PRIMARY_COLOR
      }}>
        {formatNumber(avgVolume1, translate)}
      </span>
      {' '}{translate('trendPage.marketTrends.narratives.search.metrics.average')}
      <span style={{
        margin: '0 8px',
        color: '#D0D0D0',
        fontWeight: '300'
      }}>｜</span>
      {translate('trendPage.filters.set2')}: {' '}
      <span style={{
        fontWeight: '700',
        fontSize: '1.05rem',
        color: BBC_STYLE.SECONDARY_COLOR
      }}>
        {formatNumber(avgVolume2, translate)}
      </span>
      {' '}{translate('trendPage.marketTrends.narratives.search.metrics.average')}
      {avgDiffPercentage > 20 && (
        <>
          <span style={{
            margin: '0 10px',
            color: '#D0D0D0',
            fontWeight: '300'
          }}>｜</span>
          <span style={{
            fontWeight: '600',
            fontSize: '0.9rem',
            color: BBC_STYLE.HIGHLIGHT_COLOR,
            backgroundColor: 'rgba(213, 94, 0, 0.08)',
            padding: '2px 6px',
            borderRadius: '3px'
          }}>
            {translate('trendPage.marketTrends.narratives.search.comparison.avgDiff', {
              percentage: avgDiffPercentage
            })}
          </span>
        </>
      )}
    </>
  );
};

const SearchChart = memo(({ chartData, hasSecondaryData, domainRange, translate, maxPrimaryIndex, maxSecondaryIndex }) => {
  const primaryKey = translate('trendPage.filters.set1');
  const secondaryKey = translate('trendPage.filters.set2');

  return (
    <ResponsiveContainer width="100%" height={350}>
      <LineChart
        data={chartData}
        margin={BBC_STYLE.MARGIN}
      >
        <XAxis
          dataKey="period"
          tick={{ fontSize: BBC_STYLE.AXIS_SIZE, fill: BBC_STYLE.AXIS_COLOR }}
          axisLine={{ stroke: BBC_STYLE.AXIS_COLOR }}
          tickLine={false}
        />
        <YAxis
          domain={[domainRange.min, domainRange.max]}
          tick={{ fontSize: BBC_STYLE.AXIS_SIZE, fill: BBC_STYLE.AXIS_COLOR }}
          tickFormatter={(value) => value.toFixed(1)}
          axisLine={false}
          tickLine={false}
          label={{
            value: translate('trendPage.marketTrends.searchChart.yAxisLabel'),
            angle: -90,
            position: 'insideLeft',
            style: {
              fontSize: BBC_STYLE.AXIS_SIZE,
              fill: BBC_STYLE.AXIS_COLOR,
              textAnchor: 'middle'
            }
          }}
        />
        <Tooltip
          cursor={{ stroke: 'rgba(206, 206, 206, 0.3)' }}
          content={({ active, payload, label }) => {
            if (!active || !payload || !payload.length) return null;

            return (
              <div style={{
                background: 'white',
                borderRadius: '8px',
                padding: '12px 16px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
                minWidth: '140px'
              }}>
                <div style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: '#999999',
                  marginBottom: '10px'
                }}>
                  {label}
                </div>
                {payload.map((entry, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '3px 0',
                      gap: '16px'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: entry.color
                      }} />
                      <span style={{ fontSize: '11px', color: '#666666' }}>
                        {entry.name}
                      </span>
                    </div>
                    <span style={{
                      fontSize: '11px',
                      fontWeight: '500',
                      fontFamily: BBC_STYLE.FONT_FAMILY,
                      color: BBC_STYLE.TEXT_COLOR
                    }}>
                      {entry.value.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            );
          }}
        />

        {/* Primary line */}
        <Line
          type="monotone"
          dataKey={primaryKey}
          stroke={BBC_STYLE.PRIMARY_COLOR}
          strokeWidth={2.5}
          dot={false}
          activeDot={{ r: 5, fill: BBC_STYLE.PRIMARY_COLOR }}
        />

        {/* Peak annotation for primary */}
        {maxPrimaryIndex !== -1 && (
          <ReferenceDot
            x={chartData[maxPrimaryIndex]?.period}
            y={chartData[maxPrimaryIndex]?.[primaryKey]}
            r={5}
            fill={BBC_STYLE.HIGHLIGHT_COLOR}
            stroke="#fff"
            strokeWidth={2}
          />
        )}

        {/* Secondary line if available */}
        {hasSecondaryData && (
          <Line
            type="monotone"
            dataKey={secondaryKey}
            stroke={BBC_STYLE.SECONDARY_COLOR}
            strokeWidth={2.5}
            dot={false}
            activeDot={{ r: 5, fill: BBC_STYLE.SECONDARY_COLOR }}
          />
        )}

        {/* Peak annotation for secondary */}
        {hasSecondaryData && maxSecondaryIndex !== -1 && (
          <ReferenceDot
            x={chartData[maxSecondaryIndex]?.period}
            y={chartData[maxSecondaryIndex]?.[secondaryKey]}
            r={5}
            fill={BBC_STYLE.SECONDARY_COLOR}
            stroke="#fff"
            strokeWidth={2}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
});

SearchChart.propTypes = {
  chartData: PropTypes.arrayOf(PropTypes.object).isRequired,
  hasSecondaryData: PropTypes.bool.isRequired,
  domainRange: PropTypes.shape({
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired
  }).isRequired,
  translate: PropTypes.func.isRequired,
  maxPrimaryIndex: PropTypes.number.isRequired,
  maxSecondaryIndex: PropTypes.number
};

const SearchTrendChart = ({ searchData }) => {
  const { translate } = useLanguage();

  const { domainRange, chartData, primaryData, secondaryData, maxPrimaryIndex, maxSecondaryIndex } = useMemo(() => {
    if (!searchData?.primary?.search_trends?.time_series?.trend_data?.log_values) {
      return {
        domainRange: { min: 0, max: 0 },
        chartData: [],
        primaryData: null,
        secondaryData: null,
        maxPrimaryIndex: -1,
        maxSecondaryIndex: -1
      };
    }

    const primaryData = searchData.primary.search_trends.time_series;
    const secondaryData = searchData.secondary?.search_trends?.time_series;
    const hasSecondaryData = !!secondaryData?.trend_data?.log_values;

    // Omit first and last data points for cleaner chart edges
    const primaryLabels = primaryData.trend_data.labels.slice(1, -1);
    const primaryValues = primaryData.trend_data.log_values.slice(1, -1);
    const secondaryValues = hasSecondaryData ? secondaryData.trend_data.log_values.slice(1, -1) : [];
    const allValues = [...primaryValues, ...secondaryValues];

    const min = Math.min(...allValues);
    const max = Math.max(...allValues);

    const padding = (max - min) * 0.05;
    const domainRange = {
      min: Math.floor(min - padding),
      max: Math.ceil(max + padding)
    };

    // Find max indices
    const maxPrimaryValue = Math.max(...primaryValues);
    const maxPrimaryIndex = primaryValues.indexOf(maxPrimaryValue);
    const maxSecondaryIndex = hasSecondaryData ? secondaryValues.indexOf(Math.max(...secondaryValues)) : -1;

    const chartData = primaryLabels.map((label, index) => ({
      period: label,
      [translate('trendPage.filters.set1')]: primaryValues[index],
      ...(hasSecondaryData ? {
        [translate('trendPage.filters.set2')]: secondaryValues[index]
      } : {})
    }));

    return {
      domainRange,
      chartData,
      primaryData,
      secondaryData,
      maxPrimaryIndex,
      maxSecondaryIndex
    };
  }, [searchData, translate]);

  if (!searchData?.primary?.search_trends?.time_series?.trend_data?.log_values) {
    return (
      <div className="search-chart-error">
        {translate('trendPage.marketTrends.errors.noSearchData')}
      </div>
    );
  }

  const hasSecondaryData = !!secondaryData;

  return (
    <ChartContainer>
      {/* Narrative Title with visual effects */}
      <h2 style={{
        fontSize: '1.1rem',
        fontWeight: '600',
        color: BBC_STYLE.TEXT_COLOR,
        margin: '0 0 8px 0',
        lineHeight: '1.4'
      }}>
        {createNarrativeTitle(primaryData, secondaryData, hasSecondaryData, translate)}
      </h2>

      {/* Subtitle with insights and highlighting */}
      <div style={{
        fontSize: '0.85rem',
        color: BBC_STYLE.TEXT_COLOR,
        margin: '0 0 20px 0',
        lineHeight: '1.5'
      }}>
        {createSubtitle(primaryData, secondaryData, hasSecondaryData, translate)}
      </div>

      {/* Filter labels when comparing */}
      {hasSecondaryData && (
        <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <div style={{
              width: '16px',
              height: '3px',
              backgroundColor: BBC_STYLE.PRIMARY_COLOR,
              borderRadius: '2px'
            }} />
            <span style={{ fontSize: '12px', color: BBC_STYLE.TEXT_COLOR }}>
              {translate('trendPage.filters.set1')}
            </span>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <div style={{
              width: '16px',
              height: '3px',
              backgroundColor: BBC_STYLE.SECONDARY_COLOR,
              borderRadius: '2px'
            }} />
            <span style={{ fontSize: '12px', color: BBC_STYLE.TEXT_COLOR }}>
              {translate('trendPage.filters.set2')}
            </span>
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="chart-wrapper" style={{ flex: 1, overflow: 'hidden' }}>
        <SearchChart
          chartData={chartData}
          hasSecondaryData={hasSecondaryData}
          domainRange={domainRange}
          translate={translate}
          maxPrimaryIndex={maxPrimaryIndex}
          maxSecondaryIndex={maxSecondaryIndex}
        />
      </div>
    </ChartContainer>
  );
};

SearchTrendChart.propTypes = {
  searchData: PropTypes.shape({
    primary: PropTypes.shape({
      search_trends: PropTypes.shape({
        time_series: PropTypes.shape({
          trend_data: PropTypes.shape({
            labels: PropTypes.arrayOf(PropTypes.string),
            log_values: PropTypes.arrayOf(PropTypes.number)
          }),
          max_period: PropTypes.shape({
            labels: PropTypes.string,
            values: PropTypes.number
          }),
          min_period: PropTypes.shape({
            labels: PropTypes.string,
            values: PropTypes.number
          }),
          average: PropTypes.number,
          trend: PropTypes.shape({
            direction: PropTypes.string,
            percentage: PropTypes.number
          })
        })
      })
    }),
    secondary: PropTypes.shape({
      search_trends: PropTypes.shape({
        time_series: PropTypes.shape({
          trend_data: PropTypes.shape({
            labels: PropTypes.arrayOf(PropTypes.string),
            log_values: PropTypes.arrayOf(PropTypes.number)
          }),
          max_period: PropTypes.shape({
            labels: PropTypes.string,
            values: PropTypes.number
          }),
          min_period: PropTypes.shape({
            labels: PropTypes.string,
            values: PropTypes.number
          }),
          average: PropTypes.number,
          trend: PropTypes.shape({
            direction: PropTypes.string,
            percentage: PropTypes.number
          })
        })
      })
    })
  }).isRequired
};

export default memo(SearchTrendChart);
